import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { API_CODES } from "./api-codes";
import { ToastService } from "../toast.service";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private toast: ToastService,
  ) {}

  /**
   * Check if localStorage is available.
   * Safe for SSR environments.
   */
  private isLocalStorageAvailable(): boolean {
    return typeof window !== "undefined" && !!window.localStorage;
  }

  /**
   * Retrieve the current access token.
   */
  private getToken(): string | null {
    if (this.isLocalStorageAvailable()) {
      return localStorage.getItem("access_token");
    }

    return null;
  }

  /**
   * Build standard authenticated JSON headers.
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.getToken();

    let headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Build headers for binary/file downloads.
   *
   * We intentionally do NOT set:
   *
   * Content-Type: application/json
   *
   * because a PDF download is not a JSON
   * request/response.
   */
  private getDownloadHeaders(
    skipAuth = false,
    customHeaders?: Record<string, string>,
  ): HttpHeaders {
    let headers = new HttpHeaders();

    if (customHeaders) {
      for (const [key, value] of Object.entries(customHeaders)) {
        headers = headers.set(key, value);
      }
    }

    if (!skipAuth) {
      const token = this.getToken();

      if (token) {
        headers = headers.set("Authorization", `Bearer ${token}`);
      }
    }

    return headers;
  }

  /**
   * Display the standard API error.
   */
  private handleApiError(
    error: any,
    showErrorToast = true,
  ): Observable<never> {
    if (showErrorToast) {
      const code = error?.status;

      const message =
        API_CODES[code] || "An unexpected error occurred.";

      this.toast.error(
        "Something went wrong",
        message,
      );
    }

    // Preserve Django's original response for component-level handling.
    return throwError(() => error);
  }

  /**
   * Universal JSON request handler.
   */
  request<T>(
    url: string,
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    payload?: any,
    successMsg?: string,
    customHeaders?: Record<string, string>,
    options?: {
      skipAuth?: boolean;
      showErrorToast?: boolean;
    },
  ): Observable<T> {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    /*
     * Preserve the existing custom-header
     * behavior.
     */
    if (customHeaders && Object.keys(customHeaders).length > 0) {
      headers = headers.set("Content-Type", "application/json");

      for (const [key, value] of Object.entries(customHeaders)) {
        headers = headers.set(key, value);
      }

      /*
       * Also preserve authentication unless
       * explicitly disabled.
       */
      if (!options?.skipAuth) {
        const token = this.getToken();

        if (token) {
          headers = headers.set("Authorization", `Bearer ${token}`);
        }
      }
    } else if (!options?.skipAuth) {
      headers = this.getAuthHeaders();
    }

    let httpCall: Observable<T>;

    switch (method) {
      case "GET":
        httpCall = this.http.get<T>(url, {
          headers,
        });
        break;

      case "POST":
        httpCall = this.http.post<T>(url, payload, {
          headers,
        });
        break;

      case "PUT":
        httpCall = this.http.put<T>(url, payload, {
          headers,
        });
        break;

      case "PATCH":
        httpCall = this.http.patch<T>(url, payload, {
          headers,
        });
        break;

      case "DELETE":
        httpCall = this.http.delete<T>(url, {
          headers,
        });
        break;

      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return httpCall.pipe(
      tap(() => {
        if (successMsg) {
          /*
           * Existing success handling can
           * remain here if you want to
           * re-enable the toast later.
           */
          // Swal.fire({
          //   title: "Success!",
          //   text: successMsg,
          //   icon: "success",
          //   timer: 4000,
          //   showConfirmButton: false,
          //   toast: true,
          //   position: "top-end",
          //   customClass: {
          //     container:
          //       "!z-[999999999]",
          //   },
          // });
        }
      }),

      catchError((error) =>
        this.handleApiError(
          error,
          options?.showErrorToast !== false,
        ),
      ),
    );
  }

  /**
   * Download a protected file.
   *
   * Used for binary responses such as
   * invoice PDFs.
   *
   * Example:
   *
   * this.apiService
   *   .download(url)
   *   .subscribe((blob) => {
   *     ...
   *   });
   */
  download(
    url: string,
    options?: {
      skipAuth?: boolean;
      customHeaders?: Record<string, string>;
    },
  ): Observable<Blob> {
    const headers = this.getDownloadHeaders(
      options?.skipAuth ?? false,
      options?.customHeaders,
    );

    return this.http
      .get(url, {
        headers,
        responseType: "blob",
      })
      .pipe(catchError((error) => this.handleApiError(error)));
  }
}
